import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_BASE_URL;
const emptyDraft = { name: "", description: "", display_order: 0, is_visible: true };

const CategoryManager = () => {
  const [categories, setCategories] = useState([]);
  const [draft, setDraft] = useState(emptyDraft);
  const [message, setMessage] = useState("");
  const token = localStorage.getItem("token");
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const load = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/categories`, { headers });
      setCategories(response.data.categories || []);
    } catch {
      setMessage("Categories could not be loaded.");
    }
  }, [headers]);

  useEffect(() => {
    load();
  }, [load]);

  const create = async (event) => {
    event.preventDefault();
    try {
      await axios.post(`${API_URL}/api/categories`, draft, { headers });
      setDraft(emptyDraft);
      setMessage("Category created.");
      load();
    } catch (error) {
      setMessage(error.response?.data?.error || "Category could not be created.");
    }
  };

  const updateLocal = (id, field, value) => {
    setCategories((current) => current.map((category) => (
      category.id === id ? { ...category, [field]: value } : category
    )));
  };

  const save = async (category) => {
    try {
      await axios.patch(`${API_URL}/api/categories/${category.id}`, {
        name: category.name,
        description: category.description,
        display_order: Number(category.display_order),
        is_visible: Boolean(category.is_visible),
      }, { headers });
      setMessage(`${category.name} saved.`);
      load();
    } catch (error) {
      setMessage(error.response?.data?.error || "Category could not be saved.");
    }
  };

  const remove = async (category) => {
    if (!window.confirm(`Delete “${category.name}”?`)) return;
    try {
      await axios.delete(`${API_URL}/api/categories/${category.id}`, { headers });
      setMessage("Category deleted.");
      load();
    } catch (error) {
      setMessage(error.response?.data?.error || "Category could not be deleted.");
    }
  };

  return (
    <section className="mb-12 border border-gray-200 bg-white p-5 shadow-sm">
      <div className="mb-5">
        <p className="text-xs uppercase tracking-[0.2em] text-gray-500">Portfolio settings</p>
        <h2 className="mt-1 text-2xl font-semibold text-gray-900">Photography categories</h2>
        <p className="mt-1 text-sm text-gray-600">Visible categories appear on the public Collections page in this order.</p>
      </div>
      {message && <p className="mb-4 text-sm text-gray-700" role="status">{message}</p>}
      <div className="space-y-3">
        {categories.map((category) => (
          <div key={category.id} className="grid gap-3 border-t border-gray-100 pt-4 md:grid-cols-[5rem_1fr_2fr_auto]">
            <input
              type="number"
              aria-label={`${category.name} display order`}
              value={category.display_order}
              onChange={(event) => updateLocal(category.id, "display_order", event.target.value)}
              className="border px-2 py-2"
            />
            <input
              aria-label="Category name"
              value={category.name}
              onChange={(event) => updateLocal(category.id, "name", event.target.value)}
              className="border px-3 py-2"
            />
            <textarea
              aria-label={`${category.name} description`}
              value={category.description || ""}
              onChange={(event) => updateLocal(category.id, "description", event.target.value)}
              rows="2"
              className="border px-3 py-2"
            />
            <div className="flex flex-wrap items-center gap-2">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={Boolean(category.is_visible)}
                  onChange={(event) => updateLocal(category.id, "is_visible", event.target.checked)}
                />
                Visible
              </label>
              <button onClick={() => save(category)} className="bg-gray-900 px-3 py-2 text-sm text-white">Save</button>
              <button onClick={() => remove(category)} className="px-2 py-2 text-sm text-red-600">Delete</button>
              <span className="w-full text-xs text-gray-500">{category.gallery_count} galleries · /{category.slug}</span>
            </div>
          </div>
        ))}
      </div>
      <form onSubmit={create} className="mt-7 grid gap-3 border-t border-gray-200 pt-5 md:grid-cols-[1fr_2fr_6rem_auto]">
        <input
          required
          placeholder="New category"
          value={draft.name}
          onChange={(event) => setDraft({ ...draft, name: event.target.value })}
          className="border px-3 py-2"
        />
        <input
          placeholder="Description"
          value={draft.description}
          onChange={(event) => setDraft({ ...draft, description: event.target.value })}
          className="border px-3 py-2"
        />
        <input
          type="number"
          aria-label="Display order"
          value={draft.display_order}
          onChange={(event) => setDraft({ ...draft, display_order: Number(event.target.value) })}
          className="border px-2 py-2"
        />
        <button className="bg-blue-600 px-4 py-2 text-white">Add category</button>
      </form>
    </section>
  );
};

export default CategoryManager;
